'use client';

import Image from '@/components/Image';
import { EPlatform } from '@/utils/enums/platform-enum';
import { useEffect, useRef } from 'react';
import { Autoplay, Navigation } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';

// 根据Vue版本定义PhotoWall数据结构
interface PhotoWallItem {
  type: '1' | '2'; // 1: 图片, 2: 视频
  link: string; // 图片或视频链接
  poster_img?: string; // 视频封面图
}

interface PhotoWallData {
  title: string;
  pc_hong_bg_image?: string; // PC端背景图
  wap_hong_bg_image?: string; // WAP端背景图
  list: PhotoWallItem[];
}

interface DivaPhotoWallProps {
  pageData: { photo_wall: PhotoWallData };
  deviceType: EPlatform;
}

export default function DivaPhotoWall({ pageData, deviceType }: DivaPhotoWallProps) {
  const swiperRef = useRef<any>(null);
  const videoObserverRef = useRef<IntersectionObserver | null>(null);

  const isWap = deviceType !== EPlatform.WEB;
  const photoWallData = pageData.photo_wall;

  // Vue版本中的updateSlides方法 - 重置轮播到第一张
  const updateSlides = () => {
    if (swiperRef.current?.swiper) {
      swiperRef.current.swiper.slideTo(0, 0, false);
    }
  };

  // 暴露给外部的方法（如果需要）
  useEffect(() => {
    // 模拟Vue版本中的$refs.PhotoWall.updateSlides()调用
    if (typeof window !== 'undefined') {
      (window as any).divaPhotoWallUpdateSlides = updateSlides;
    }

    return () => {
      if (typeof window !== 'undefined') {
        delete (window as any).divaPhotoWallUpdateSlides;
      }
    };
  }, []);

  // 视频自动播放观察器设置
  useEffect(() => {
    const observeVideos = () => {
      if (videoObserverRef.current) {
        videoObserverRef.current.disconnect();
      }

      setTimeout(
        () => {
          const options = {
            threshold: 0.8,
          };

          videoObserverRef.current = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
              const video = entry.target as HTMLVideoElement;
              if (entry.isIntersecting) {
                // 当前视频进入视口区域
                if (video.paused) {
                  video.play().catch(() => {
                    // 忽略视频播放失败
                  });
                }
              } else if (!video.paused) {
                // 当前视频离开视口区域
                video.pause();
              }
            });
          }, options);

          // 观察所有视频元素
          const videos = document.querySelectorAll('.video-slide');
          videos.forEach((video) => {
            videoObserverRef.current?.observe(video);
          });
        },
        isWap ? 3000 : 2000,
      ); // 与Vue版本的延迟保持一致
    };

    observeVideos();

    return () => {
      if (videoObserverRef.current) {
        videoObserverRef.current.disconnect();
      }
    };
  }, [isWap]);

  // 安全检查：如果没有图片数据，返回null
  if (!photoWallData?.list || photoWallData.list.length === 0) {
    return null;
  }

  if (isWap) {
    // 移动端PhotoWall - 基于WAP版本PhotoWall.vue
    return (
      <div
        className="diva-photo-wall w-[375px] h-[590px] relative mx-auto"
        style={{
          background: `url('${photoWallData.wap_hong_bg_image || ''}') no-repeat center / contain`,
        }}
      >
        <p
          className="diva-photo-wall-title font-nohemi font-normal text-[12px] text-[#dcb883] leading-[18px] pt-[40px] text-center"
          dangerouslySetInnerHTML={{ __html: photoWallData.title }}
        />

        <div className="photo-swiper absolute bottom-[30px] left-0 w-full">
          <Swiper
            ref={swiperRef}
            modules={photoWallData.list.length > 2 ? [Autoplay] : []}
            spaceBetween={16}
            slidesPerView={2.3}
            slidesOffsetBefore={14}
            loop={photoWallData.list.length > 2}
            autoplay={
              photoWallData.list.length > 2
                ? {
                    delay: 3000,
                    stopOnLastSlide: false,
                    disableOnInteraction: false,
                    pauseOnMouseEnter: true,
                  }
                : false
            }
            className="w-full"
          >
            {photoWallData.list.map((item, index) => (
              <SwiperSlide key={index} className="photo-item">
                {item.type === '1' ? (
                  <Image
                    src={item.link}
                    alt={`Zeelool optical eyeglasses online store diva-eyeglasses-photo-wall${index + 1}`}
                    width={150}
                    height={200}
                    className="w-full"
                  />
                ) : (
                  <div className="video-slide-out overflow-hidden relative">
                    <video
                      className="video-slide w-full h-auto"
                      poster={item.poster_img}
                      loop
                      muted
                      playsInline
                    >
                      <source src={item.link} />
                    </video>
                    {/* 视频边框效果 */}
                    <div className="absolute inset-0 border border-[#0f0f0f] box-border pointer-events-none" />
                  </div>
                )}
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      </div>
    );
  }

  // PC端PhotoWall - 基于PC版本PhotoWall.vue
  return (
    <div
      className="photo-list dmp-photo-wall relative w-full h-[60.31vw] pt-[5.21vw] pb-[8.85vw] box-border"
      style={{
        backgroundImage: `url('${photoWallData.pc_hong_bg_image || ''}')`,
        backgroundRepeat: 'no-repeat',
        backgroundSize: '100%',
      }}
    >
      <p
        className="photo-text mx-auto font-br font-normal text-[1.25vw] text-[#dcb883] leading-[1.88vw] text-center"
        dangerouslySetInnerHTML={{ __html: photoWallData.title }}
      />

      <div className="photo-swiper mt-[23.44vw] box-border">
        <Swiper
          ref={swiperRef}
          modules={photoWallData.list.length > 5 ? [Navigation, Autoplay] : [Navigation]}
          spaceBetween={60}
          slidesPerView={5}
          slidesOffsetBefore={115}
          slidesPerGroup={1}
          loop={photoWallData.list.length > 5}
          autoplay={
            photoWallData.list.length > 5
              ? {
                  delay: 3000,
                  stopOnLastSlide: false,
                  disableOnInteraction: false,
                  pauseOnMouseEnter: true,
                }
              : false
          }
          navigation={{
            nextEl: '.photo-next',
            prevEl: '.photo-prev',
          }}
          className="w-full !pt-[2.08vw] !pb-[2.08vw]"
        >
          {photoWallData.list.map((item, index) => (
            <SwiperSlide key={item.link + index} className="photo-item">
              {item.type === '1' ? (
                <Image
                  src={item.link}
                  alt={`Zeelool optical eyeglasses online store diva-eyeglasses-photo-wall${index + 1}`}
                  width={364}
                  height={488}
                  className={`w-full ${index % 2 === 0 ? 'rotate-[-4deg]' : 'rotate-[4deg]'}`}
                />
              ) : (
                <div
                  className={`video-slide-out overflow-hidden relative ${index % 2 === 0 ? 'rotate-[-4deg]' : 'rotate-[4deg]'}`}
                >
                  <video className="video-slide w-full h-full" poster={item.poster_img} loop muted>
                    <source src={item.link} />
                  </video>
                  {/* 视频边框效果 */}
                  <div className="absolute inset-0 border-4 border-[#0f0f0f] box-border pointer-events-none" />
                </div>
              )}
            </SwiperSlide>
          ))}
        </Swiper>

        {/* 导航按钮 - 只在有超过5个项目时显示 */}
        {photoWallData.list.length > 5 && (
          <>
            <div className="photo-prev tab-div absolute bottom-[-1.56vw] right-[8.85vw] flex items-center justify-center w-[2.08vw] h-[2.08vw] rounded-full bg-white text-primary-black cursor-pointer transition-colors swiper-button-disabled:bg-white/30 swiper-button-disabled:cursor-not-allowed">
              {/* 使用SVG图标替代iconfont */}
              <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
                <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z" />
              </svg>
            </div>
            <div className="photo-next tab-div absolute bottom-[-1.56vw] right-[5.21vw] flex items-center justify-center w-[2.08vw] h-[2.08vw] rounded-full bg-white text-primary-black cursor-pointer transition-colors swiper-button-disabled:bg-white/30 swiper-button-disabled:cursor-not-allowed">
              {/* 使用SVG图标替代iconfont */}
              <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
                <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z" />
              </svg>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
